import React, { useState } from 'react'
import styled from 'styled-components'
import APIList, { APIListWrapper } from '../components/APIList'
import DownLoadIcon from '../asset/DownLoadIcon'
import { gql, useMutation, useQuery } from '@apollo/client'
import { SMPLineChart } from '../components/graph/SMPLineChart'
import DataUploadPopup from '../components/DataUploadPopup'
import { downloadSheet, parseSmpFile, SmpUploadData } from '../util/dataFile'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px #00000029;
  overflow: hidden;
`

const Header = styled.div`
  padding: 16px 24px 16px 24px;
  background: #373a40;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex-shrink: 0;
`

const Title = styled.div`
  font-family: SUIT;
  font-size: 22px;
  font-weight: 700;
  line-height: 27px;
  letter-spacing: 0em;
  text-align: center;
  color: #ffffff;
`

const BodyWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  gap: 16px;
  align-items: center;
`

const GraphBox = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border-radius: 4px;
  gap: 12px;
`

const Bottom = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 24px;
  justify-content: center;
`

const ButtonBox = styled.div`
  display: flex;
  padding: 8px;
  border-radius: 4px;
  gap: 4px;
  align-items: center;
  cursor: pointer;
`

const ButtonText = styled.div`
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  text-align: left;
  color: #4d4d4d;
`

export const findAllSmpGQL = gql`
  query findAllSmp {
    findAllSmp {
      _id
      year
      data {
        date
        value
        maxSmp
        minSmp
        weightedAver
      }
    }
  }
`

const createSmpGQL = gql`
  mutation createSmp($val: CreateSmpInput!) {
    createSmp(createSmpInput: $val) {
      _id
      year
      data {
        date
        value
        maxSmp
        minSmp
        weightedAver
      }
    }
  }
`

export const DataSMP = () => {
  const {
    data: OriginSmpData,
    loading,
    error,
    refetch,
  } = useQuery(findAllSmpGQL)
  const [createSmp] = useMutation(createSmpGQL)
  const [selected, setSelected] = useState<string | number | null>(null)
  const [showUploadPopup, setShowUploadPopup] = useState(false)

  if (loading) return <p>Loading...</p>
  if (error) return <p>Error : {error.message}</p>

  const smpData: any = Object.values(OriginSmpData)[0] || []

  const filteredData = smpData
    .filter((v: any) => String(v.year) === String(selected))
    .map((v: any) => v.data)
    .flat()

  const handleDownload = () => {
    if (!selected || filteredData.length === 0) return

    const escapeCsv = (value: string | number | null | undefined) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`

    const header = '날짜,smp,일 최대,일 최소,일 가중평균'
    const rows = filteredData.map((item: any) =>
      [
        escapeCsv(item.date),
        escapeCsv(item.value),
        escapeCsv(item.maxSmp),
        escapeCsv(item.minSmp),
        escapeCsv(item.weightedAver),
      ].join(',')
    )
    const csvContent = [header, ...rows].join('\n')

    const blob = new Blob(['\ufeff', csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `smp-${selected}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDownloadSample = () => {
    const sampleSource =
      smpData.find((item: any) => String(item.year) === String(selected)) ||
      smpData[0]

    if (!sampleSource?.data?.length) return

    const sampleRows = sampleSource.data.map((item: any) => ({
      날짜: item.date,
      smp: item.value,
      '일 최대': item.maxSmp,
      '일 최소': item.minSmp,
      '일 가중평균': item.weightedAver,
    }))

    downloadSheet(sampleRows, 'SMP', `smp-sample-${sampleSource.year}.xlsx`)
  }

  const handleUploadSubmit = async ({
    name,
    data,
  }: {
    name: string
    data: SmpUploadData[]
  }) => {
    await createSmp({
      variables: {
        val: {
          year: name,
          data,
        },
      },
    })
    await refetch()
    setSelected(name)
  }

  return (
    <>
      <APIListWrapper>
        <APIList
          noApi
          dataList={smpData && smpData?.map((v: any) => v.year)}
          setSelected={setSelected}
          fileFunc={() => setShowUploadPopup(true)}
        />
      </APIListWrapper>
      <DataUploadPopup<SmpUploadData[]>
        isOpen={showUploadPopup}
        menuName="SMP"
        nameField="year"
        existingNames={smpData?.map((v: any) => v.year)}
        onClose={() => setShowUploadPopup(false)}
        onDownloadSample={handleDownloadSample}
        parseFile={parseSmpFile}
        onSubmit={handleUploadSubmit}
      />
      <ContentWrapper>
        <Header>
          <Title>SMP</Title>
        </Header>
        <BodyWrapper>
          {selected && (
            <Body>
              <GraphBox>
                <SMPLineChart
                  title="smp"
                  data={filteredData}
                  width="100%"
                  height="100%"
                />
              </GraphBox>
              <Bottom>
                <ButtonBox onClick={handleDownload}>
                  <DownLoadIcon />
                  <ButtonText>다운로드</ButtonText>
                </ButtonBox>
                {/* <ButtonBox>
                  <DeleteIcon />
                  <ButtonText>삭제</ButtonText>
                </ButtonBox> */}
              </Bottom>
            </Body>
          )}
        </BodyWrapper>
      </ContentWrapper>
    </>
  )
}
